import { useWebphoneStore } from "@/store/webphoneStore";

const RELOAD_TS_KEY = "chunkReload:lastReloadAt";
const RELOAD_GUARD_MS = 10_000;

let reloadPending = false;

/**
 * Reload the page to recover from a stale chunk — unless a call is active.
 *
 * A full reload tears down the embedded webphone and would drop a live call, so
 * we never reload while `hasActiveCall` is true. Instead we mark the reload
 * pending and flush it when the call ends (see the store subscription below).
 * The sessionStorage timestamp guards against an infinite reload loop when a
 * chunk is genuinely missing (real broken build).
 */
function attemptReload(): void {
    const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0);

    if (Date.now() - last < RELOAD_GUARD_MS) {
        return;
    }

    if (useWebphoneStore.getState().hasActiveCall) {
        reloadPending = true;
        return;
    }

    reloadPending = false;
    sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
    window.location.reload();
}

/**
 * Recover from stale Vite chunks after a deploy.
 *
 * When a tab stays open across a frontend deploy, Vite rotates content-hashed
 * chunk filenames and deletes the old ones. The next lazy `import()` then 404s
 * with "Failed to fetch dynamically imported module" and the page goes blank.
 *
 * Vite compiles every dynamic import to its preload helper, which dispatches a
 * `vite:preloadError` event on failure. We reload once to pull a fresh
 * `index.html` + current hashes; the URL is preserved, so the user lands on the
 * module they navigated to. Reloads are deferred while a call is active so they
 * never interrupt the webphone.
 */
export function initChunkReload(): void {
    window.addEventListener("vite:preloadError", () => {
        attemptReload();
    });

    useWebphoneStore.subscribe((state, prev) => {
        if (reloadPending && prev.hasActiveCall && !state.hasActiveCall) {
            attemptReload();
        }
    });
}
